import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Edit, Calendar, Clock, MapPin, Video, Check, X, User, Mail, Phone } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUS_COLORS = { requested: "text-yellow-400 bg-yellow-900/20", confirmed: "text-green-400 bg-green-900/20", pending_payment: "text-orange-400 bg-orange-900/20", completed: "text-blue-400 bg-blue-900/20", rescheduled: "text-purple-400 bg-purple-900/20", canceled: "text-red-400 bg-red-900/20", "no_show": "text-zinc-400 bg-zinc-800" };

export default function AdminBooking() {
  const [tab, setTab] = useState("appointments");
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editService, setEditService] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [st, sv, av, bl, ap] = await Promise.all([
        http.get("/booking/admin/settings"),
        http.get("/booking/admin/services"),
        http.get("/booking/admin/availability"),
        http.get("/booking/admin/blocked-dates"),
        http.get("/booking/admin/appointments"),
      ]);
      setSettings(st.data); setServices(sv.data); setAvailability(av.data); setBlockedDates(bl.data); setAppointments(ap.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (upd) => {
    try { const { data } = await http.patch("/booking/admin/settings", upd); setSettings(data); toast.success("Saved"); } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const tabs = [
    { key: "appointments", label: "Appointments", count: appointments.filter(a => !["canceled","completed","no_show"].includes(a.status)).length },
    { key: "services", label: "Services", count: services.length },
    { key: "availability", label: "Availability" },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-booking">
      <h2 className="font-display text-3xl text-[#F4F1EA]">Booking System</h2>
      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`booking-tab-${t.key}`}
            className={`px-4 py-2 text-xs font-ui tracking-[0.15em] uppercase border transition-colors ${tab === t.key ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]/50"}`}>
            {t.label} {t.count !== undefined && <span className="ml-1 opacity-60">({t.count})</span>}
          </button>
        ))}
      </div>

      {tab === "appointments" && <AppointmentsTab appointments={appointments} onReload={load} />}
      {tab === "services" && <ServicesTab services={services} onReload={load} editService={editService} setEditService={setEditService} />}
      {tab === "availability" && <AvailabilityTab availability={availability} blockedDates={blockedDates} onReload={load} />}
      {tab === "settings" && <SettingsTab settings={settings} saveSettings={saveSettings} />}
    </div>
  );
}

/* ═══ APPOINTMENTS ═══ */
function AppointmentsTab({ appointments, onReload }) {
  const updateApt = async (id, updates) => {
    try { await http.patch(`/booking/admin/appointments/${id}`, updates); toast.success("Updated"); onReload(); } catch { toast.error("Failed"); }
  };

  if (!appointments.length) return <div className="p-10 text-center text-[#8a857d] text-sm border border-white/5 bg-[#0a0a0a]">No appointments yet.</div>;

  return (
    <div className="space-y-3">
      {appointments.map((a, i) => (
        <div key={a.id} className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3" data-testid={`apt-row-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-ui text-sm text-[#F4F1EA] font-bold">{a.client_name}</h3>
              <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#8a857d]">
                <span className="flex items-center gap-1"><Mail size={12} /> {a.client_email}</span>
                {a.client_phone && <span className="flex items-center gap-1"><Phone size={12} /> {a.client_phone}</span>}
              </div>
            </div>
            <div className="text-right">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_COLORS[a.status] || "text-zinc-400 bg-zinc-800"}`}>{a.status}</span>
              <div className="text-[10px] text-[#8a857d] mt-1">{a.payment_status}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-[#d1cfc7]">
            <span className="flex items-center gap-1"><Calendar size={12} className="text-[#C9A055]" /> {a.date}</span>
            <span className="flex items-center gap-1"><Clock size={12} className="text-[#C9A055]" /> {a.start_time} - {a.end_time}</span>
            <span className="flex items-center gap-1">{a.format === "office" ? <MapPin size={12} className="text-[#C9A055]" /> : <Video size={12} className="text-[#C9A055]" />} {a.format}</span>
            <span className="text-[#C9A055]">{a.service_title}</span>
          </div>
          {a.reason_for_booking && <p className="text-xs text-[#8a857d]"><span className="text-[#5a5650]">Reason:</span> {a.reason_for_booking}</p>}
          {a.hopes && <p className="text-xs text-[#8a857d]"><span className="text-[#5a5650]">Hopes:</span> {a.hopes}</p>}
          <div className="flex gap-2 flex-wrap pt-1 border-t border-white/5">
            {a.status === "requested" && <Btn onClick={() => updateApt(a.id, { status: "confirmed" })} icon={<Check size={12} />} text="Confirm" />}
            {a.status === "confirmed" && <Btn onClick={() => updateApt(a.id, { status: "completed" })} icon={<Check size={12} />} text="Complete" />}
            {!["canceled","completed","no_show"].includes(a.status) && <Btn onClick={() => updateApt(a.id, { status: "canceled" })} icon={<X size={12} />} text="Cancel" red />}
            {!["canceled","completed","no_show"].includes(a.status) && <Btn onClick={() => updateApt(a.id, { status: "no_show" })} text="No Show" red />}
            {a.format === "zoom" && (
              <input placeholder="Zoom link..." defaultValue={a.zoom_link} onBlur={e => updateApt(a.id, { zoom_link: e.target.value })} className="stc-input text-xs flex-1 min-w-[200px]" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function Btn({ onClick, icon, text, red }) {
  return <button onClick={onClick} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-colors ${red ? "border-red-800/50 text-red-400 hover:bg-red-900/30" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]/50"}`}>{icon} {text}</button>;
}

/* ═══ SERVICES ═══ */
function ServicesTab({ services, onReload, editService, setEditService }) {
  const blank = { title: "", short_desc: "", full_desc: "", price: 0, deposit_amount: 0, duration: 45, buffer_time: 15, in_office_enabled: true, zoom_enabled: false, max_bookings_per_slot: 1, cancellation_policy: "", reschedule_policy: "", payment_timing: "full_upfront", confirmation_message: "", follow_up_instructions: "", active: true, sort_order: 0 };

  const save = async (data) => {
    try {
      if (data.id) { await http.patch(`/booking/admin/services/${data.id}`, data); }
      else { await http.post("/booking/admin/services", data); }
      toast.success("Service saved"); setEditService(null); onReload();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; try { await http.delete(`/booking/admin/services/${id}`); toast.success("Deleted"); onReload(); } catch { toast.error("Failed"); } };

  if (editService) return <ServiceForm svc={editService} onSave={save} onCancel={() => setEditService(null)} />;

  return (
    <div className="space-y-3">
      <button onClick={() => setEditService({ ...blank })} className="btn-gold text-xs" data-testid="add-service"><Plus size={14} /> Add Service</button>
      {services.map(s => (
        <div key={s.id} className="bg-[#0a0a0a] border border-white/10 p-4 flex items-center gap-4" data-testid={`svc-row-${s.id}`}>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-ui text-sm text-[#F4F1EA] font-bold">{s.title}</h3>
              {!s.active && <span className="text-[9px] bg-red-900/30 text-red-400 px-1.5 py-0.5">INACTIVE</span>}
            </div>
            <div className="flex gap-3 text-[10px] text-[#8a857d] mt-1">
              <span>{s.duration}min</span>
              <span>${s.price}</span>
              <span>{s.payment_timing}</span>
              {s.in_office_enabled && <span>Office</span>}
              {s.zoom_enabled && <span>Zoom</span>}
            </div>
          </div>
          <button onClick={() => setEditService({ ...s })} className="btn-outline px-2"><Edit size={12} /></button>
          <button onClick={() => del(s.id)} className="btn-outline px-2 border-red-900/50 text-red-500"><Trash2 size={12} /></button>
        </div>
      ))}
    </div>
  );
}

function ServiceForm({ svc, onSave, onCancel }) {
  const [f, setF] = useState({ ...svc });
  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-4" data-testid="service-form">
      <div className="flex justify-between items-center"><h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">{f.id ? "Edit" : "New"} Service</h3><button onClick={onCancel} className="text-[#8a857d]"><X size={16} /></button></div>
      <Field label="Title" value={f.title} onChange={v => setF({ ...f, title: v })} />
      <Field label="Short Description" value={f.short_desc} onChange={v => setF({ ...f, short_desc: v })} />
      <Field label="Full Description" value={f.full_desc} onChange={v => setF({ ...f, full_desc: v })} textarea rows={4} />
      <div className="grid grid-cols-4 gap-4">
        <PriceField label="Price" value={f.price} onChange={v => setF({ ...f, price: v })} />
        <PriceField label="Deposit" value={f.deposit_amount} onChange={v => setF({ ...f, deposit_amount: v })} />
        <NumField label="Duration (min)" value={f.duration} onChange={v => setF({ ...f, duration: v })} />
        <NumField label="Buffer (min)" value={f.buffer_time} onChange={v => setF({ ...f, buffer_time: v })} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Payment Timing</label>
          <select value={f.payment_timing} onChange={e => setF({ ...f, payment_timing: e.target.value })} className="stc-input w-full text-sm">
            <option value="full_upfront">Full Payment Upfront</option>
            <option value="deposit">Deposit Required</option>
            <option value="after">Pay After Session</option>
            <option value="in_person">Pay In Person</option>
            <option value="free">Free / Complimentary</option>
          </select>
        </div>
        <NumField label="Max Bookings/Slot" value={f.max_bookings_per_slot} onChange={v => setF({ ...f, max_bookings_per_slot: v })} />
      </div>
      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-xs text-[#8a857d]"><input type="checkbox" checked={f.in_office_enabled} onChange={e => setF({ ...f, in_office_enabled: e.target.checked })} className="accent-[#C9A055]" /> In-Office</label>
        <label className="flex items-center gap-2 text-xs text-[#8a857d]"><input type="checkbox" checked={f.zoom_enabled} onChange={e => setF({ ...f, zoom_enabled: e.target.checked })} className="accent-[#C9A055]" /> Zoom</label>
        <label className="flex items-center gap-2 text-xs text-[#8a857d]"><input type="checkbox" checked={f.active} onChange={e => setF({ ...f, active: e.target.checked })} className="accent-[#C9A055]" /> Active</label>
      </div>
      <Field label="Cancellation Policy" value={f.cancellation_policy} onChange={v => setF({ ...f, cancellation_policy: v })} textarea rows={2} />
      <Field label="Confirmation Message" value={f.confirmation_message} onChange={v => setF({ ...f, confirmation_message: v })} textarea rows={2} />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-outline text-xs px-4">Cancel</button>
        <button onClick={() => onSave(f)} className="btn-gold text-xs" data-testid="save-service"><Save size={12} /> {f.id ? "Update" : "Create"}</button>
      </div>
    </div>
  );
}

/* ═══ AVAILABILITY ═══ */
function AvailabilityTab({ availability, blockedDates, onReload }) {
  const [newSlot, setNewSlot] = useState({ day_of_week: 0, start_time: "09:00", end_time: "17:00", format: "both" });
  const [newBlock, setNewBlock] = useState({ date: "", reason: "", all_day: true });

  const addSlot = async () => {
    try { await http.post("/booking/admin/availability", newSlot); toast.success("Added"); onReload(); } catch { toast.error("Failed"); }
  };
  const delSlot = async (id) => { await http.delete(`/booking/admin/availability/${id}`); onReload(); };
  const addBlock = async () => {
    if (!newBlock.date) return;
    try { await http.post("/booking/admin/blocked-dates", newBlock); toast.success("Blocked"); setNewBlock({ date: "", reason: "", all_day: true }); onReload(); } catch { toast.error("Failed"); }
  };
  const delBlock = async (id) => { await http.delete(`/booking/admin/blocked-dates/${id}`); onReload(); };
  const seedAvail = async () => { try { const { data } = await http.post("/booking/admin/seed-availability"); toast.success(data.message); onReload(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-6">
      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <div className="flex justify-between items-center"><h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">Weekly Availability</h3><button onClick={seedAvail} className="btn-outline text-[10px]">Seed M-F 9-5</button></div>
        {availability.length === 0 && <p className="text-[#8a857d] text-xs">No availability set. Click "Seed M-F 9-5" to start with defaults.</p>}
        {availability.map(a => (
          <div key={a.id} className="flex items-center gap-3 text-xs text-[#d1cfc7] border border-white/5 px-3 py-2">
            <span className="font-bold w-24">{DAYS[a.day_of_week]}</span>
            <span>{a.start_time} - {a.end_time}</span>
            <span className="text-[#C9A055]">{a.format}</span>
            <button onClick={() => delSlot(a.id)} className="ml-auto text-red-500 hover:text-red-400"><Trash2 size={12} /></button>
          </div>
        ))}
        <div className="flex gap-2 items-end flex-wrap pt-2 border-t border-white/5">
          <div><label className="block text-[10px] text-[#8a857d] mb-1">Day</label><select value={newSlot.day_of_week} onChange={e => setNewSlot({ ...newSlot, day_of_week: parseInt(e.target.value) })} className="stc-input text-sm">{DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></div>
          <div><label className="block text-[10px] text-[#8a857d] mb-1">Start</label><input type="time" value={newSlot.start_time} onChange={e => setNewSlot({ ...newSlot, start_time: e.target.value })} className="stc-input text-sm" /></div>
          <div><label className="block text-[10px] text-[#8a857d] mb-1">End</label><input type="time" value={newSlot.end_time} onChange={e => setNewSlot({ ...newSlot, end_time: e.target.value })} className="stc-input text-sm" /></div>
          <div><label className="block text-[10px] text-[#8a857d] mb-1">Format</label><select value={newSlot.format} onChange={e => setNewSlot({ ...newSlot, format: e.target.value })} className="stc-input text-sm"><option value="both">Both</option><option value="office">Office</option><option value="zoom">Zoom</option></select></div>
          <button onClick={addSlot} className="btn-gold text-[10px]"><Plus size={12} /> Add</button>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">Blocked Dates</h3>
        {blockedDates.map(b => (
          <div key={b.id} className="flex items-center gap-3 text-xs text-[#d1cfc7] border border-white/5 px-3 py-2">
            <span className="font-bold">{b.date}</span>
            <span className="text-[#8a857d]">{b.reason || "Blocked"}</span>
            <button onClick={() => delBlock(b.id)} className="ml-auto text-red-500"><Trash2 size={12} /></button>
          </div>
        ))}
        <div className="flex gap-2 items-end flex-wrap pt-2 border-t border-white/5">
          <div><label className="block text-[10px] text-[#8a857d] mb-1">Date</label><input type="date" value={newBlock.date} onChange={e => setNewBlock({ ...newBlock, date: e.target.value })} className="stc-input text-sm" /></div>
          <div><label className="block text-[10px] text-[#8a857d] mb-1">Reason</label><input value={newBlock.reason} onChange={e => setNewBlock({ ...newBlock, reason: e.target.value })} className="stc-input text-sm" placeholder="Vacation, holiday..." /></div>
          <button onClick={addBlock} className="btn-gold text-[10px]"><Plus size={12} /> Block</button>
        </div>
      </div>
    </div>
  );
}

/* ═══ SETTINGS ═══ */
function SettingsTab({ settings, saveSettings }) {
  const [s, setS] = useState(settings);
  return (
    <div className="space-y-4">
      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">Office Information</h3>
        <Field label="Office Name" value={s.office_name} onChange={v => setS({ ...s, office_name: v })} />
        <Field label="Address" value={s.office_address} onChange={v => setS({ ...s, office_address: v })} textarea rows={2} />
        <Field label="Arrival Instructions" value={s.arrival_instructions} onChange={v => setS({ ...s, arrival_instructions: v })} textarea rows={2} />
        <Field label="Parking Info" value={s.parking_info} onChange={v => setS({ ...s, parking_info: v })} />
        <Field label="Building Entry" value={s.building_entry} onChange={v => setS({ ...s, building_entry: v })} />
        <Field label="Waiting Room" value={s.waiting_room} onChange={v => setS({ ...s, waiting_room: v })} />
        <SaveBtn onClick={() => saveSettings({ office_name: s.office_name, office_address: s.office_address, arrival_instructions: s.arrival_instructions, parking_info: s.parking_info, building_entry: s.building_entry, waiting_room: s.waiting_room })} />
      </div>
      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">Booking Rules</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <NumField label="Min Notice (hours)" value={s.min_booking_notice_hours} onChange={v => setS({ ...s, min_booking_notice_hours: v })} />
          <NumField label="Max Advance (days)" value={s.max_advance_booking_days} onChange={v => setS({ ...s, max_advance_booking_days: v })} />
          <NumField label="Cancel Hours" value={s.cancellation_hours} onChange={v => setS({ ...s, cancellation_hours: v })} />
          <NumField label="Reschedule Hours" value={s.reschedule_hours} onChange={v => setS({ ...s, reschedule_hours: v })} />
        </div>
        <NumField label="Default Buffer (min)" value={s.default_buffer_minutes} onChange={v => setS({ ...s, default_buffer_minutes: v })} />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[#8a857d]"><input type="checkbox" checked={s.zoom_enabled_global} onChange={e => setS({ ...s, zoom_enabled_global: e.target.checked })} className="accent-[#C9A055]" /> Enable Zoom Globally</label>
        </div>
        <SaveBtn onClick={() => saveSettings({ min_booking_notice_hours: s.min_booking_notice_hours, max_advance_booking_days: s.max_advance_booking_days, cancellation_hours: s.cancellation_hours, reschedule_hours: s.reschedule_hours, default_buffer_minutes: s.default_buffer_minutes, zoom_enabled_global: s.zoom_enabled_global })} />
      </div>
      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">Booking Page</h3>
        <Field label="Headline" value={s.booking_page_headline} onChange={v => setS({ ...s, booking_page_headline: v })} />
        <Field label="Subheadline" value={s.booking_page_subheadline} onChange={v => setS({ ...s, booking_page_subheadline: v })} />
        <SaveBtn onClick={() => saveSettings({ booking_page_headline: s.booking_page_headline, booking_page_subheadline: s.booking_page_subheadline })} />
      </div>
    </div>
  );
}

/* ═══ SHARED ═══ */
function Field({ label, value, onChange, textarea, rows = 3 }) {
  const Tag = textarea ? "textarea" : "input";
  return <div><label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">{label}</label><Tag value={value || ""} onChange={e => onChange(e.target.value)} className="stc-input w-full" {...(textarea ? { rows } : {})} /></div>;
}
function PriceField({ label, value, onChange }) {
  return <div><label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">{label}</label><div className="flex items-center gap-1"><span className="text-[#8a857d]">$</span><input type="number" step="0.01" value={value || 0} onChange={e => onChange(parseFloat(e.target.value) || 0)} className="stc-input w-full text-sm" /></div></div>;
}
function NumField({ label, value, onChange }) {
  return <div><label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">{label}</label><input type="number" value={value || 0} onChange={e => onChange(parseInt(e.target.value) || 0)} className="stc-input w-full text-sm" /></div>;
}
function SaveBtn({ onClick }) {
  return <div className="flex justify-end"><button onClick={onClick} className="btn-gold text-[10px]"><Save size={12} /> Save</button></div>;
}
